
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { User, Lock, Mail, Info, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";

const formSchema = z
  .object({
    nome: z.string().min(3, "O nome deve ter pelo menos 3 caracteres"),
    email: z.string().email("E-mail inválido"),
    password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof formSchema>;

const Cadastro = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      // Simulação de envio de email com link de aprovação
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Email que ficará responsável pela aprovação
      const approvalEmail = "financeiro@sunjob.tech";
      
      // Aqui seria enviado o email com link de aprovação na implementação real
      console.log(`Solicitação de cadastro enviada para aprovação: ${approvalEmail}`);
      console.log(`Dados do usuário: ${values.nome} (${values.email})`);
      
      // Mostrar mensagem de sucesso e não redirecionar para login
      setIsRegistrationComplete(true);
      toast.success("Solicitação de cadastro enviada com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao realizar cadastro");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-hero-blue to-hero-green p-4">
      <div className="absolute top-4 left-4">
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-white hover:bg-white/10"
          onClick={() => navigate("/")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>
      
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img 
            src="/lovable-uploads/39686db0-4967-4556-a4f9-65cbf5776672.png" 
            alt="Logo" 
            className="h-24 w-auto drop-shadow-lg" 
          />
        </div>
        
        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm rounded-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-hero-blue">Criar Conta</CardTitle>
            <CardDescription className="text-gray-600">
              Preencha os dados para solicitar acesso
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {isRegistrationComplete ? (
              <div className="space-y-6">
                <div className="rounded-lg bg-green-50 p-6 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-green-800 mb-2">Solicitação Enviada!</h3>
                  <p className="text-sm text-green-700">
                    Sua solicitação de cadastro foi enviada com sucesso! Um email será enviado para o setor financeiro para aprovação. 
                    Você receberá um email quando sua conta for aprovada.
                  </p>
                </div>
                <Button 
                  className="w-full bg-hero-blue hover:bg-hero-blue-dark text-white"
                  onClick={() => navigate("/")}
                >
                  Voltar para página inicial
                </Button>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="nome"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">Nome Completo</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-400">
                              <User className="h-4 w-4" />
                            </span>
                            <Input 
                              placeholder="Seu nome" 
                              {...field} 
                              className="pl-10 bg-white/80 border-gray-200 focus:ring-hero-blue focus:border-hero-blue"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">E-mail</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-400">
                              <Mail className="h-4 w-4" />
                            </span>
                            <Input 
                              placeholder="seu@email.com" 
                              {...field} 
                              className="pl-10 bg-white/80 border-gray-200 focus:ring-hero-blue focus:border-hero-blue"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">Senha</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-400">
                              <Lock className="h-4 w-4" />
                            </span>
                            <Input 
                              type="password" 
                              placeholder="••••••" 
                              {...field} 
                              className="pl-10 bg-white/80 border-gray-200 focus:ring-hero-blue focus:border-hero-blue"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">Confirmar Senha</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-400">
                              <Lock className="h-4 w-4" />
                            </span>
                            <Input 
                              type="password" 
                              placeholder="••••••" 
                              {...field} 
                              className="pl-10 bg-white/80 border-gray-200 focus:ring-hero-blue focus:border-hero-blue"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Alert className="bg-hero-blue/10 border-hero-blue/20">
                    <Info className="h-4 w-4 text-hero-blue" />
                    <AlertDescription className="text-sm text-gray-700">
                      Após o cadastro, sua solicitação será enviada para aprovação.
                      Você receberá um email quando sua conta for aprovada.
                    </AlertDescription>
                  </Alert>
                  <Button 
                    type="submit" 
                    className="w-full bg-hero-blue hover:bg-hero-blue-dark text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Enviando solicitação..." : "Solicitar Cadastro"}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
          <CardFooter className="text-center pt-0">
            <p className="text-sm text-gray-600 w-full">
              Já possui uma conta?{" "}
              <Link to="/login" className="text-hero-blue hover:underline hover:text-hero-blue-dark font-medium">
                Faça login
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Cadastro;
